import { jwtDecode } from "jwt-decode";
import PropTypes from "prop-types";
import { useCallback, useRef, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import useClientRect from "hooks/use-client-rect";
import useGoogleIdentity from "hooks/use-google-identity";
import Sentry from "utils/sentry";

import { Box, Skeleton } from "components/core";

const NOOP = () => {};

// renderButton only accepts an absolute pixel width, capped by Google at 400.
const MAX_BUTTON_WIDTH = 400;

/**
 * Google GSI 'Sign in / Sign up with Google' button. Only one of these should
 * be added to a page, as there are hidden globals used to store the callbacks.
 * */
const GoogleButtonGSI = ({
  width,
  fullWidth = false,
  disabled = false,
  onIdTokenAndPayload,
  onClick = NOOP,
  initializeProps = {},
  buttonOptions = {},
}) => {
  const [isLoading, setLoading] = useState(true);
  const [containerRect, containerRef] = useClientRect();
  const buttonWidth = fullWidth
    ? Math.min(MAX_BUTTON_WIDTH, Math.floor(containerRect?.width || 0)) ||
      undefined
    : width;
  const handleToken = useCallback(
    (data) => {
      const payload = jwtDecode(data.credential);
      onIdTokenAndPayload(data.credential, payload);
    },
    [onIdTokenAndPayload],
  );
  const idApi = useGoogleIdentity({
    callback: handleToken,
    autoSelect: true,
    ...initializeProps,
  });
  const buttonParentRef = useRef();

  useDeepCompareEffect(() => {
    // see https://developers.google.com/identity/gsi/web/reference/js-reference#google.accounts.id.renderButton
    const parent = buttonParentRef.current;

    if (idApi && parent && !(fullWidth && !buttonWidth)) {
      const options = {
        logo_alignment: "left",
        shape: "pill",
        size: "large",
        theme: "outline",
        type: "standard",
        width: buttonWidth,
        click_listener: onClick,
        ...buttonOptions,
      };

      try {
        idApi.renderButton(parent, options);
        setLoading(false);
      } catch (e) {
        Sentry.captureException(e);
      }
    }

    return () => {
      // Remove the button
      if (parent) {
        parent?.firstChild?.remove();
      }
    };
  }, [idApi, buttonParentRef, buttonOptions, onClick, buttonWidth, fullWidth]);

  return (
    <Box
      ref={fullWidth ? containerRef : undefined}
      alignSelf={fullWidth ? "stretch" : "center"}
      sx={{ ...(disabled && { pointerEvents: "none" }) }}
    >
      {isLoading && (
        <Skeleton
          height={40}
          sx={{ borderRadius: 999 }}
          variant="rounded"
          width={fullWidth ? "100%" : width}
        />
      )}
      <Box ref={buttonParentRef} width="100%" />
    </Box>
  );
};

GoogleButtonGSI.propTypes = {
  /**
   * called with (string id token, object decoded payload) if user
   * successfully signs in with google
   */
  onIdTokenAndPayload: PropTypes.func.isRequired,
  /** passed to `renderButton` */
  buttonOptions: PropTypes.object,
  /** disables point events on the button */
  disabled: PropTypes.bool,
  /**
   * sizes the button to fill its container (up to Google's 400px
   * maximum), taking precedence over `width`
   */
  fullWidth: PropTypes.bool,
  /** passed to `initialize` */
  initializeProps: PropTypes.object,
  /**
   * absolute width of the button, must be whole
   * number of pixles
   */
  width: PropTypes.number,
  /**
   * called when button clicked
   */
  onClick: PropTypes.func,
};

export default GoogleButtonGSI;
